import type { SystemModule, ModuleCategory } from "@/types/modules";
import { MODULE_CATEGORIES } from "@/types/modules";
import {
  PLAN_TYPES,
  type PlanFormData,
  emptyFormData,
} from "../hooks/usePlanManager";

interface PlanCreateFormProps {
  form: PlanFormData;
  setForm: React.Dispatch<React.SetStateAction<PlanFormData>>;
  showModuleSelector: boolean;
  setShowModuleSelector: (show: boolean) => void;
  moduleSearchTerm: string;
  setModuleSearchTerm: (term: string) => void;
  modules: SystemModule[] | undefined;
  groupedModules: Record<ModuleCategory, SystemModule[]>;
  toggleModule: (
    code: string,
    setter: React.Dispatch<React.SetStateAction<PlanFormData>>,
  ) => void;
  isCreating: boolean;
  handleSubmit: () => Promise<void>;
  onClose: () => void;
}

export function PlanCreateForm({
  form,
  setForm,
  showModuleSelector,
  setShowModuleSelector,
  moduleSearchTerm,
  setModuleSearchTerm,
  modules,
  groupedModules,
  toggleModule,
  isCreating,
  handleSubmit,
  onClose,
}: PlanCreateFormProps) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-6 py-4 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            Create New Plan
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Configure pricing tiers, usage limits, and feature modules for your
            subscription plans.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600"
        >
          Close
        </button>
      </div>

      <div className="p-6 space-y-6">
        {/* 1. Basic Information */}
        <div className="space-y-4">
          <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[10px] font-bold text-white">
              1
            </span>
            Basic Information
          </h4>
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-600">
                Plan Name <span className="text-red-500">*</span>
              </label>
              <input
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g., Starter, Growth, Professional"
                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-100"
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-600">
                Plan Type <span className="text-red-500">*</span>
              </label>
              <select
                value={form.type}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, type: e.target.value }))
                }
                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-100"
              >
                {PLAN_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-slate-400">
                Determines the plan tier and available features
              </p>
            </div>
            <div className="space-y-1.5 lg:col-span-1">
              <label className="block text-xs font-semibold text-slate-600">
                Trial Period
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  value={form.trialDays ?? 0}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      trialDays: Number(e.target.value),
                    }))
                  }
                  className="h-10 w-full rounded-lg border border-slate-200 px-3 pr-12 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-100"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                  days
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-slate-600">
              Description
            </label>
            <textarea
              value={form.description ?? ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
              placeholder="Brief description of what this plan offers..."
              rows={2}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-100 resize-none"
            />
          </div>
        </div>

        {/* 2. Pricing */}
        <div className="space-y-4 border-t border-slate-100 pt-6">
          <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[10px] font-bold text-white">
              2
            </span>
            Pricing
          </h4>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-600">
                Monthly Price (₹)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">
                  ₹
                </span>
                <input
                  type="number"
                  min="0"
                  value={form.priceMonthly ?? 0}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      priceMonthly: Number(e.target.value),
                    }))
                  }
                  placeholder="0"
                  className="h-10 w-full rounded-lg border border-slate-200 pl-7 pr-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-100"
                />
              </div>
              <p className="text-[10px] text-slate-400">
                Set to 0 for free plans
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-600">
                Yearly Price (₹)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">
                  ₹
                </span>
                <input
                  type="number"
                  min="0"
                  value={form.priceYearly ?? 0}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      priceYearly: Number(e.target.value),
                    }))
                  }
                  placeholder="0"
                  className="h-10 w-full rounded-lg border border-slate-200 pl-7 pr-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-100"
                />
              </div>
              <p className="text-[10px] text-slate-400">
                Usually offers 10-20% discount over monthly
              </p>
            </div>
          </div>
        </div>

        {/* 3. Usage Limits */}
        <div className="space-y-4 border-t border-slate-100 pt-6">
          <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[10px] font-bold text-white">
              3
            </span>
            Usage Limits
          </h4>
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-600">
                Maximum Stores
              </label>
              <input
                type="number"
                min="-1"
                value={form.maxStores}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    maxStores: Number(e.target.value),
                  }))
                }
                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-100"
              />
              <p className="text-[10px] text-slate-400">
                Use -1 for unlimited stores
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-600">
                Maximum Users
              </label>
              <input
                type="number"
                min="-1"
                value={form.maxUsers}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    maxUsers: Number(e.target.value),
                  }))
                }
                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-100"
              />
              <p className="text-[10px] text-slate-400">
                Use -1 for unlimited users
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-600">
                Storage Quota
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  value={form.storageGB}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      storageGB: Number(e.target.value),
                    }))
                  }
                  className="h-10 w-full rounded-lg border border-slate-200 px-3 pr-10 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-100"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                  GB
                </span>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-600">
                Max Offline Days
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  value={form.maxOfflineDays ?? 7}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      maxOfflineDays: Number(e.target.value),
                    }))
                  }
                  className="h-10 w-full rounded-lg border border-slate-200 px-3 pr-12 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-100"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                  days
                </span>
              </div>
              <p className="text-[10px] text-slate-400">
                Maximum days clients can operate offline before re-validation
              </p>
            </div>
          </div>
        </div>

        {/* 4. Features & Marketing */}
        <div className="space-y-4 border-t border-slate-100 pt-6">
          <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[10px] font-bold text-white">
              4
            </span>
            Features & Marketing
          </h4>
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-slate-600">
              Feature Highlights
            </label>
            <input
              value={(form.features ?? []).join(", ")}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  features: e.target.value
                    .split(",")
                    .map((item) => item.trim())
                    .filter(Boolean),
                }))
              }
              placeholder="e.g., Unlimited invoices, Priority support, Advanced reports"
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-100"
            />
            <p className="text-[10px] text-slate-400">
              Comma-separated list of features to display on pricing page
            </p>
          </div>
        </div>

        {/* 5. Module Assignment */}
        <div className="space-y-4 border-t border-slate-100 pt-6">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[10px] font-bold text-white">
                5
              </span>
              Feature Modules
              <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                {form.selectedModules.length} selected
              </span>
            </h4>
            <button
              type="button"
              onClick={() => setShowModuleSelector(!showModuleSelector)}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
            >
              {showModuleSelector ? "Hide Modules" : "Select Modules"}
            </button>
          </div>
          <p className="text-xs text-slate-500 -mt-2">
            Assign feature modules that tenants on this plan will have access
            to.
          </p>

          {showModuleSelector && (
            <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <input
                type="text"
                value={moduleSearchTerm}
                onChange={(e) => setModuleSearchTerm(e.target.value)}
                placeholder="Search modules by name or code..."
                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-100"
              />
              <div className="max-h-72 overflow-y-auto rounded-lg bg-white border border-slate-100 p-3">
                {Object.entries(groupedModules).length === 0 ? (
                  <p className="py-4 text-center text-sm text-slate-500">
                    No modules found. Go to Feature Modules to seed default
                    modules.
                  </p>
                ) : (
                  Object.entries(groupedModules).map(([category, mods]) => (
                    <div key={category} className="mb-4 last:mb-0">
                      <div
                        className={`mb-2 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold ${
                          MODULE_CATEGORIES[category as ModuleCategory]
                            ?.color || "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {MODULE_CATEGORIES[category as ModuleCategory]?.label ||
                          category}
                        <span className="opacity-60">({mods.length})</span>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {mods.map((mod) => (
                          <label
                            key={mod.code}
                            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                              form.selectedModules.includes(mod.code)
                                ? "border-slate-900 bg-slate-50"
                                : "border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={form.selectedModules.includes(mod.code)}
                              onChange={() => toggleModule(mod.code, setForm)}
                              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-slate-700 truncate">
                                  {mod.name}
                                </span>
                                {mod.isCore && (
                                  <span className="shrink-0 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                                    Core
                                  </span>
                                )}
                              </div>
                              <code className="text-[10px] text-slate-400 block truncate">
                                {mod.code}
                              </code>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Selected modules preview */}
          {form.selectedModules.length > 0 && !showModuleSelector && (
            <div className="flex flex-wrap gap-1.5">
              {form.selectedModules.map((code) => {
                const mod = modules?.find((m) => m.code === code);
                return (
                  <span
                    key={code}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                  >
                    {mod?.name || code}
                    <button
                      type="button"
                      onClick={() => toggleModule(code, setForm)}
                      className="text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-between border-t border-slate-100 px-6 py-4 bg-slate-50 rounded-b-2xl">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={Boolean(form.isActive)}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, isActive: e.target.checked }))
            }
            className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
          />
          <span className="text-sm font-medium text-slate-600">Active</span>
          <span className="text-xs text-slate-400">(visible to tenants)</span>
        </label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setForm({ ...emptyFormData })}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-white transition-colors"
          >
            Reset
          </button>
          <button
            type="button"
            disabled={isCreating || !form.name}
            onClick={handleSubmit}
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isCreating ? "Creating..." : "Create Plan"}
          </button>
        </div>
      </div>
    </section>
  );
}
